"use client";

import posthog from "posthog-js";
import { type FormEvent, useCallback, useRef, useState } from "react";
import type { ReaderReportType } from "@/lib/reports/report-type";
import { emitClientSubscribed } from "@/lib/subscription-client";
import { useExploreRoute } from "../_lib/explore-route-context";

type SubscribeStatus = "idle" | "submitting" | "success" | "error";

interface SubscribeState {
  status: SubscribeStatus;
  message?: string;
  alreadySubscribed?: boolean;
}

interface SubscribeResponse {
  message?: string;
  alreadySubscribed?: boolean;
  error?: string;
}

/**
 * Fallback source value preserved for back-compat with historical events.
 * New callers should always pass an explicit `source` (either via the hook
 * `options.source` or per-call on subscribe/subscribeToEdition) so that we
 * can attribute conversions to specific UI surfaces.
 */
const DEFAULT_SOURCE = "explore-newsletter";

interface UseNewsletterSubscribeOptions {
  /**
   * Subscribe surface identifier sent to PostHog. Used to attribute the
   * conversion to a specific UI placement (e.g. "sidebar", "mobile_footer",
   * "header_dialog"). For surfaces that render multiple sub-variants
   * (e.g. the email wall), prefer passing `source` per-call instead.
   */
  source?: string;
}

export function useNewsletterSubscribe(
  options?: UseNewsletterSubscribeOptions
) {
  const { reportType } = useExploreRoute();
  const defaultSource = options?.source ?? DEFAULT_SOURCE;

  const [email, setEmail] = useState("");
  const [state, setState] = useState<SubscribeState>({ status: "idle" });
  const lastEmailRef = useRef("");

  const subscribeToEdition = useCallback(
    async ({
      edition,
      emailOverride,
      includeEditions,
      source,
    }: {
      edition: ReaderReportType;
      emailOverride?: string;
      /** Send multiple editions so the API adds rather than replaces */
      includeEditions?: readonly ReaderReportType[];
      /**
       * Per-call override of the subscribe surface identifier. When omitted
       * the hook's `options.source` (or the global default) is used.
       */
      source?: string;
    }): Promise<boolean> => {
      const targetEmail = emailOverride ?? lastEmailRef.current;
      if (!targetEmail) {
        return false;
      }

      setState({ status: "submitting" });

      try {
        const audience = includeEditions ?? [edition];
        const effectiveSource = source ?? defaultSource;
        const pageUrl =
          typeof window !== "undefined" ? window.location.href : undefined;
        const pagePath =
          typeof window !== "undefined" ? window.location.pathname : undefined;

        const response = await fetch("/api/subscribe", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: targetEmail,
            source: effectiveSource,
            audience,
            posthogSessionId: posthog.get_session_id?.(),
            pageUrl,
            pagePath,
          }),
        });

        const data: SubscribeResponse = await response.json();

        if (response.ok) {
          setState({
            status: "success",
            message: data.message,
            alreadySubscribed: data.alreadySubscribed,
          });
          const editionsToEmit = includeEditions ?? [edition];
          for (const e of editionsToEmit) {
            emitClientSubscribed(e);
          }

          if (!data.alreadySubscribed && typeof window !== "undefined") {
            window.fbq?.("track", "Lead");

            const url = new URL(window.location.href);
            url.searchParams.set("subscribed", "true");
            window.history.replaceState({}, "", url.toString());
          }

          return true;
        }

        setState({
          status: "error",
          message: data.error ?? "Something went wrong. Please try again.",
        });
        return false;
      } catch {
        setState({
          status: "error",
          message: "Network error. Please check your connection and try again.",
        });
        return false;
      }
    },
    [defaultSource]
  );

  const handleSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();

      const trimmedEmail = email.trim();
      if (!trimmedEmail) {
        return;
      }

      lastEmailRef.current = trimmedEmail;
      setEmail("");
      await subscribeToEdition({
        edition: reportType,
        emailOverride: trimmedEmail,
      });
    },
    [email, reportType, subscribeToEdition]
  );

  const subscribe = useCallback(
    async ({
      editions,
      source,
    }: {
      editions: readonly ReaderReportType[];
      /** Per-call override of the subscribe surface identifier. */
      source?: string;
    }) => {
      const trimmedEmail = email.trim();
      if (!trimmedEmail) {
        return;
      }

      lastEmailRef.current = trimmedEmail;
      setEmail("");
      await subscribeToEdition({
        edition: reportType,
        emailOverride: trimmedEmail,
        includeEditions: editions,
        source,
      });
    },
    [email, reportType, subscribeToEdition]
  );

  const reset = useCallback(() => {
    setState({ status: "idle" });
    setEmail("");
  }, []);

  return {
    email,
    setEmail,
    state,
    reportType,
    handleSubmit,
    subscribe,
    subscribeToEdition,
    reset,
    isSubmitting: state.status === "submitting",
    isSuccess: state.status === "success",
    isError: state.status === "error",
  };
}
